/**
 * #2945 slice 4 — which `/me` visit is the member app's OPENING screen.
 *
 * A member of a roster team opens on Team today (founder Q1 (a)); every
 * other `/me` visit is the Calendar. The opening is decided once per page
 * load, for the first `/me` index the frame renders, and only when that
 * entry is the first one of the load:
 *
 *   - the router's `default` location key (a cold load at `/me`), or
 *     history index 0 (the Mini App's `startapp` redirect replaces `/tg`);
 *   - with no `?date=` (a day link is the Calendar's);
 *   - without `KEEP_CALENDAR_STATE` in the location state. Two `/me`
 *     arrivals pass it because they mean the Calendar: Back from a stacked
 *     screen with no in-app history (`MemberAppLayout`'s `goBack` replaces
 *     to the screen's `backTo`), and the Mini App's `startapp=cal` (the
 *     chat's "Open calendar" button and the "My shifts" and "Add to my
 *     calendar" help topics).
 *
 * The decision is kept by location key, so a re-render (or React's strict
 * double render) of the same entry reads the same answer, and any later
 * entry reads false.
 */
import type { Location } from "react-router-dom";

/** Location state that keeps a `/me` arrival on the Calendar. */
export const KEEP_CALENDAR_STATE = { calendar: true } as const;

function keepsCalendar(state: unknown): boolean {
  return Boolean(state && typeof state === "object" && (state as { calendar?: unknown }).calendar);
}

/** True when this location could be the screen the app opened on. */
export function isOpeningEntry(location: Pick<Location, "key" | "search" | "state">): boolean {
  if (keepsCalendar(location.state)) return false;
  if (new URLSearchParams(location.search).has("date")) return false;
  if (location.key === "default") return true;
  const idx =
    typeof window !== "undefined"
      ? (window.history.state as { idx?: number } | null)?.idx
      : undefined;
  return idx === 0;
}

let decided: { key: string; opening: boolean } | null = null;

/**
 * The once-per-page-load decision: the first `/me` index of the load asks,
 * and only that entry (by location key) can be the opening.
 */
export function takeOpening(location: Pick<Location, "key" | "search" | "state">): boolean {
  if (decided === null) decided = { key: location.key, opening: isOpeningEntry(location) };
  return decided.key === location.key && decided.opening;
}

/** Tests only: start a fresh page load. */
export function resetOpeningForTests(): void {
  decided = null;
}
